import React, { useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon, ChevronRight } from '@heroicons/react/24/outline';
import api from "../api/api";
import Icon from "./Icon";
import ExchangeIcon from "./ExchangeIcon";

function CompanySearch({ onCompanySelect, setSearchName, clearResults, searchCompanies, disabled, isMobile=false }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [selectedRow, setSelectedRow] = useState(0);
  const containerRef = useRef(null);
  const ulRef = useRef(null);
  const skipFetchRef = useRef(false);

  // clearResults関数を登録
  useEffect(() => {
    if (clearResults) {
      clearResults(() => setResults([]));
    }
  }, [clearResults]);

  const handleKeyDown = (e) => {
    if (e.isComposing || e.keyCode === 229) return;

    if (e.key === 'Enter') {
        e.preventDefault();
        if (disabled) return;
    
        skipFetchRef.current = true; // ← フェッチ抑制！
    
        if (selectedRow === 0) {
          searchCompanies();
        } else {
          handleCompanyClick(results[selectedRow - 1].sec_code, results[selectedRow - 1].company_name);
        }
    
        // 少し時間を置いて、fetchDataを再許可（300〜500ms）
        setTimeout(() => {
          skipFetchRef.current = false;
        }, 500);
      }
    
      if (e.key === 'Escape') {
          e.preventDefault();
          setDebouncedTerm('')
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
          setSelectedRow(0)
      }

      if (e.key === 'ArrowDown') {
         setSelectedRow(prev => prev < results.length ? prev + 1 : results.length)
      }

      if (e.key === 'ArrowUp') {
          setSelectedRow(prev => prev > 0 ? prev - 1 : 0)
      }
  }

  // デバウンス処理: searchTermを遅延して更新
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 500); // 500ms遅延

    return () => clearTimeout(handler); // クリーンアップ
  }, [searchTerm]);

  // debouncedTermの変更に応じてAPIリクエストを送信
  useEffect(() => {
    const fetchData = async () => {
      if (skipFetchRef.current) return; // ← Enter直後ならスキップ
  
      if (!debouncedTerm) {
        setResults([]);
        return;
      }
  
      setIsLoading(true);
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/chat/api/search-companies-prefix-matching/?query=${debouncedTerm}`
        );
        setResults(response.data);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchData();
  }, [debouncedTerm]);
  

  const handleFocus = () => {
    setDebouncedTerm(searchTerm)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if ((containerRef.current && ulRef) && (!containerRef.current.contains(event.target) && !ulRef.current.contains(event.target) )) {
        setDebouncedTerm("");
        setResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setResults([]);
  }, [])
  // 入力変更時のハンドラー
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setSearchName(e.target.value);
  };

  const handleCompanyClick = (secCode, companyName) => {
    if (disabled) return;
    setResults([]);
    onCompanySelect(secCode, companyName);
  }
    
  return (
    <div className="w-full mr-4 ml-1 relative">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="企業名を入力"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          className={
            isMobile
              ? "w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent"
              : "search-area w-full pl-10 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
          }
          
          ref={containerRef}
        />
        {isLoading && <div className="absolute w-full py-2 px-2 top-[40px] text-gray-500 mt-2 bg-white rounded-lg shadow z-[8000">Loading...</div>}
        <ul className="absolute top-[50px] w-full bg-white shadow rounded-lg z-[8000]" ref={ulRef}>
          {results.length > 0 && (
            results.map((company, index) => (
              <li
                key={company.sec_code}
                className={`py-2 px-4 border-b last:border-none hover:bg-gray-100 cursor-pointer ${selectedRow === index + 1 && "bg-gray-100"}`}
                onClick={() => handleCompanyClick(company.sec_code, company.company_name)}
              >
                <div className="flex"> {/* 企業検索結果  */}
                  <div className=""><div className="w-[35px] h-[35px] mr-3"><Icon secCode={company.sec_code} companyImageUrl={company.image} /></div></div> {/* 企業ロゴ */}
                  <div className=""> {/* 企業名等 */}
                    <div className="">{company.company_name}</div> {/* 企業名 */}
                    <div className="flex">
                      <div className="mr-2"><ExchangeIcon exchange={company.exchangeshortname} /></div>  {/* 国? */}
                      <div>{company.exchangeshortname}：{company.sec_code}</div> {/* 取引所: 証券コード */}
                    </div> 
                  </div> 
                </div> 
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

export default CompanySearch;
